import { utils } from '@clickcease/cc-client-components-lib'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetFacebookAdAccounts } from '../../../../../api/getFacebookAdAccounts'
import { useSetSelectedFacebookAdAccounts } from '../../../../../api/setSelectedFacebookAdAccounts'
import { PaginatedCheckboxModal } from '../../../../common/PaginatedCheckboxModal/PaginatedCheckboxModal'
import { GenericPlatformEntity } from '../../../../../models'
import { useCheckboxPaginationWithInitialIDs } from '../../../../../../../hooks/pagination/useCheckboxPaginationWithInitialIDs'
import {
  DEFAULT_PAGINATION_PARAMS,
  PaginationMetadata,
  PaginationParams,
} from '../../../../../../../types/pagination'

interface SelectAccountIdPaginatedProps {
  onSubmit: () => void
  accountId: number
  onBack: () => void
  isBackDisabled?: boolean
}

export const SelectAccountIdPaginated = ({
  accountId,
  isBackDisabled,
  onSubmit,
  onBack,
}: SelectAccountIdPaginatedProps) => {
  const { t } = useTranslation('PMPlatforms')

  const [paginationParams, setPaginationParams] =
    useState<PaginationParams>(DEFAULT_PAGINATION_PARAMS)

  const { data, isFetching } = useGetFacebookAdAccounts({
    connectionId: accountId,
    pagination: paginationParams,
  })

  const getMetaDataParam = (param: keyof PaginationMetadata) =>
    utils.get(data, `metadata.${param}`, 0)

  const accounts = useMemo(() => (data && !(data instanceof Error) ? data.adAccounts : []), [data])

  const {
    sort,
    page,
    search,
    pageCount,
    setPageCount,
    setSort,
    searchPage,
    onFirstPage,
    onLastPage,
    onNextPage,
    onPrevPage,
    handleSelectAll,
    selectedOptions,
    handleCheckboxClicked,
    paginationParams: hookPaginationParams,
    selectedText,
    isAllSelected,
  } = useCheckboxPaginationWithInitialIDs({
    entities: accounts as GenericPlatformEntity[],
    totalCount: getMetaDataParam('totalCount') as number,
    serverSelectedCount: getMetaDataParam('selected') as number,
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPageCount(getMetaDataParam('pageCount')), [data]) // update page count
  useEffect(() => setPaginationParams(hookPaginationParams), [hookPaginationParams]) // update pagination params

  const { refetch } = useSetSelectedFacebookAdAccounts({
    connectionId: accountId,
    adAccountsIds: selectedOptions,
    config: {
      enabled: false,
      onSuccess: (data) => {
        if (!(data instanceof Error)) {
          onSubmit()
        }
      },
    },
  })

  return (
    <PaginatedCheckboxModal
      title={t('meta-connection-wizard.select-account-id-title')}
      searchPlaceholder={t('meta-connection-wizard.select-account-id-search-pholder')}
      buttonText={t('next-button')}
      entitiesName={t('meta-connection-wizard.ad-account')}
      onBack={onBack}
      isBackButtonDisabled={isBackDisabled}
      selectedText={selectedText}
      page={page}
      sort={sort}
      setSort={setSort}
      handleSubmit={refetch}
      handleSelectAll={handleSelectAll}
      handleSelect={handleCheckboxClicked}
      selected={selectedOptions}
      onFirstPage={onFirstPage}
      onLastPage={onLastPage}
      onNextPage={onNextPage}
      onPreviousPage={onPrevPage}
      onSearch={searchPage}
      pageCount={pageCount ?? 1}
      filterBy={search}
      options={accounts}
      modalLoading={isFetching}
      isAllSelected={isAllSelected}
    />
  )
}
